import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  Typography,
  Grid,
  CircularProgress,
  Box,
  Button,
  Snackbar,
  Alert,
  Chip,
  Badge,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Sync as SyncIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import TopNavBar from "../components/TopNavBar";
import { getCustomerById, updateCustomer } from "../api/customer";
import { getAllServiceListTypes } from "../api/listTypes";
import {
  upsertLocation,
  upsertBuilding,
  upsertDepartment,
  upsertRoom,
} from "../api/location";
import LocationModal from "../components/LocationModal";
import LocationsCollapseTable from "../components/LocationsCollapseTable";
import CustomerCard from "../components/CustomerCard";
import ConflictResolutionDialog from "../components/ConflictResolutionDialog";
import useSSESubscription from "../hooks/useSSESubscription";

export default function CustomerPageSSE() {
  const username = sessionStorage.getItem("username");
  const { customerId } = useParams();

  const [customerData, setCustomerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [listTypes, setListTypes] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [selectedConflict, setSelectedConflict] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const fetchCustomer = useCallback(async () => {
    console.log(`🏢 [CustomerPage] Starting to fetch customer ${customerId}`);
    setLoading(true);
    setError(null);

    try {
      const response = await getCustomerById(customerId);

      console.log(`🏢 [CustomerPage] Response received:`, {
        allData: response,
      });

      if (response && response.exists && response.customer) {
        setCustomerData(response.customer);
        setLastUpdated(new Date());
      } else {
        setError("Customer not found");
      }
    } catch (err) {
      //console.error(`❌ [CustomerPage] Error loading customer:`, err);
      setError("Failed to load customer");
    } finally {
      setLoading(false);
      //console.log(`🏢 [CustomerPage] Fetch complete, loading: false`);
    }
  }, [customerId]);

  // SSE message handler for this page
  const handleSSEMessage = useCallback(
    (message, conflict) => {
      console.log("CustomerPage received SSE message:", message);

      // If there's a conflict, show dialog instead of auto-updating
      if (conflict) {
        //console.log("Edit conflict detected:", conflict);
        setSelectedConflict(conflict);
        showSnackbar(
          `Edit conflict detected for ${conflict.entityType}`,
          "warning",
        );
        return; // Don't auto-update when there's a conflict
      }

      // Only handle messages relevant to this customer
      // Convert both to strings to handle type mismatch (URL param is string, SSE message might be int)
      const isRelevant = String(message.customerId) === String(customerId);

      if (!isRelevant) {
        console.log(
          `SSE message not relevant to this page (message customerId: ${message.customerId} [${typeof message.customerId}], page customerId: ${customerId} [${typeof customerId}]), ignoring.`,
        );
        return;
      }

      // Handle different types of updates by updating specific parts of state
      // instead of doing full data fetch to avoid render cycle issues
      switch (message.type) {
        case "customer_updated":
          console.log("Customer updated, updating customer info...");
          if (message.updateData && customerData) {
            setCustomerData((prevData) => ({
              ...prevData,
              ...message.updateData,
            }));
            setLastUpdated(new Date());
          }
          showSnackbar("Customer data updated", "info");
          break;
        case "location_updated":
          console.log("Location updated, updating location data...");
          if (message.updateData && customerData?.locations) {
            // Use setTimeout to defer state update and avoid React render cycle conflicts
            setTimeout(() => {
              setCustomerData((prevData) => {
                // Check if location already exists
                const existingLocationIndex = prevData.locations.findIndex(
                  (location) => location.pk_id === message.updateData.pk_id,
                );

                let updatedLocations;
                if (existingLocationIndex !== -1) {
                  // Update existing location
                  updatedLocations = prevData.locations.map((location) =>
                    location.pk_id === message.updateData.pk_id
                      ? { ...location, ...message.updateData }
                      : location,
                  );
                } else {
                  // Add new location
                  updatedLocations = [
                    ...prevData.locations,
                    message.updateData,
                  ];
                }

                return {
                  ...prevData,
                  locations: updatedLocations,
                };
              });
              setLastUpdated(new Date());
              showSnackbar("Location data updated", "info");
            }, 0);
          }
          break;
        case "building_updated":
          console.log("Building updated, updating building data...");

          if (message.updateData && customerData?.locations) {
            // Use setTimeout to defer state update and avoid React render cycle conflicts
            setTimeout(() => {
              setCustomerData((prevData) => {
                const updatedLocations = prevData.locations.map((location) => {
                  if (location.buildings) {
                    // Check if building already exists
                    const existingBuildingIndex = location.buildings.findIndex(
                      (building) => building.pk_id === message.updateData.pk_id,
                    );

                    if (existingBuildingIndex !== -1) {
                      // Update existing building
                      const updatedBuildings = location.buildings.map(
                        (building) =>
                          building.pk_id === message.updateData.pk_id
                            ? { ...building, ...message.updateData }
                            : building,
                      );
                      return { ...location, buildings: updatedBuildings };
                    } else if (
                      location.pk_id === message.updateData.location_pk_id
                    ) {
                      // Add new building to the correct location
                      return {
                        ...location,
                        buildings: [...location.buildings, message.updateData],
                      };
                    }
                  } else if (
                    location.pk_id === message.updateData.location_pk_id
                  ) {
                    // Location exists but has no buildings array yet
                    return { ...location, buildings: [message.updateData] };
                  }
                  return location;
                });

                // Also update the flat buildings array for the UI component
                let updatedBuildings = [...(prevData.buildings || [])];
                const existingBuildingIndex = updatedBuildings.findIndex(
                  (building) => building.pk_id === message.updateData.pk_id,
                );

                if (existingBuildingIndex !== -1) {
                  // Update existing building in flat array
                  updatedBuildings[existingBuildingIndex] = {
                    ...updatedBuildings[existingBuildingIndex],
                    ...message.updateData,
                  };
                } else {
                  // Add new building to flat array
                  updatedBuildings.push(message.updateData);
                }

                return {
                  ...prevData,
                  locations: updatedLocations,
                  buildings: updatedBuildings,
                };
              });
              setLastUpdated(new Date());
              showSnackbar("Building data updated", "info");
            }, 0);
          }
          break;
        case "department_updated":
          console.log("Department updated, updating department data...");
          if (message.updateData && customerData?.locations) {
            // Use setTimeout to defer state update and avoid React render cycle conflicts
            setTimeout(() => {
              setCustomerData((prevData) => {
                const updatedLocations = prevData.locations.map((location) => {
                  if (location.buildings) {
                    const updatedBuildings = location.buildings.map(
                      (building) => {
                        if (building.departments) {
                          // Check if department already exists
                          const existingDeptIndex =
                            building.departments.findIndex(
                              (dept) => dept.pk_id === message.updateData.pk_id,
                            );

                          if (existingDeptIndex !== -1) {
                            // Update existing department
                            const updatedDepartments = building.departments.map(
                              (dept) =>
                                dept.pk_id === message.updateData.pk_id
                                  ? { ...dept, ...message.updateData }
                                  : dept,
                            );
                            return {
                              ...building,
                              departments: updatedDepartments,
                            };
                          } else if (
                            building.pk_id === message.updateData.building_pk_id
                          ) {
                            // Add new department to the correct building
                            return {
                              ...building,
                              departments: [
                                ...building.departments,
                                message.updateData,
                              ],
                            };
                          }
                        } else if (
                          building.pk_id === message.updateData.building_pk_id
                        ) {
                          // Building exists but has no departments array yet
                          return {
                            ...building,
                            departments: [message.updateData],
                          };
                        }
                        return building;
                      },
                    );
                    return { ...location, buildings: updatedBuildings };
                  }
                  return location;
                });

                // Also update the flat departments array for the UI component
                let updatedDepartments = [...(prevData.departments || [])];
                const existingDeptIndex = updatedDepartments.findIndex(
                  (dept) => dept.pk_id === message.updateData.pk_id,
                );

                if (existingDeptIndex !== -1) {
                  // Update existing department in flat array
                  updatedDepartments[existingDeptIndex] = {
                    ...updatedDepartments[existingDeptIndex],
                    ...message.updateData,
                  };
                } else {
                  // Add new department to flat array
                  updatedDepartments.push(message.updateData);
                }

                return {
                  ...prevData,
                  locations: updatedLocations,
                  departments: updatedDepartments,
                };
              });
              setLastUpdated(new Date());
              showSnackbar("Department data updated", "info");
            }, 0);
          }
          break;
        case "room_updated":
          console.log("Room updated, updating room data...");
          if (message.updateData && customerData?.locations) {
            // Use setTimeout to defer state update and avoid React render cycle conflicts
            setTimeout(() => {
              setCustomerData((prevData) => {
                const updatedLocations = prevData.locations.map((location) => {
                  if (location.buildings) {
                    const updatedBuildings = location.buildings.map(
                      (building) => {
                        if (building.departments) {
                          const updatedDepartments = building.departments.map(
                            (dept) => {
                              // Update department areas if hierarchyAreas is provided
                              if (
                                message.updateData.hierarchyAreas &&
                                dept.pk_id ===
                                  message.updateData.hierarchyAreas.departmentId
                              ) {
                                dept = {
                                  ...dept,
                                  department_area:
                                    message.updateData.hierarchyAreas.totalArea,
                                  department_baseline_cleaning_area:
                                    message.updateData.hierarchyAreas
                                      .totalBaselineArea,
                                };
                              }

                              if (dept.rooms) {
                                // Check if room already exists
                                const existingRoomIndex = dept.rooms.findIndex(
                                  (room) =>
                                    room.pk_id === message.updateData.pk_id,
                                );

                                if (existingRoomIndex !== -1) {
                                  // Update existing room
                                  const updatedRooms = dept.rooms.map((room) =>
                                    room.pk_id === message.updateData.pk_id
                                      ? { ...room, ...message.updateData }
                                      : room,
                                  );
                                  return { ...dept, rooms: updatedRooms };
                                } else if (
                                  dept.pk_id ===
                                  message.updateData.department_pk_id
                                ) {
                                  // Add new room to the correct department
                                  return {
                                    ...dept,
                                    rooms: [...dept.rooms, message.updateData],
                                  };
                                }
                              } else if (
                                dept.pk_id ===
                                message.updateData.department_pk_id
                              ) {
                                // Department exists but has no rooms array yet
                                return { ...dept, rooms: [message.updateData] };
                              }
                              return dept;
                            },
                          );
                          return {
                            ...building,
                            departments: updatedDepartments,
                          };
                        }
                        return building;
                      },
                    );
                    return { ...location, buildings: updatedBuildings };
                  }
                  return location;
                });

                // Also update the flat rooms array for the UI component
                let updatedRooms = [...(prevData.rooms || [])];
                const existingRoomIndex = updatedRooms.findIndex(
                  (room) => room.pk_id === message.updateData.pk_id,
                );

                if (existingRoomIndex !== -1) {
                  // Update existing room in flat array
                  updatedRooms[existingRoomIndex] = {
                    ...updatedRooms[existingRoomIndex],
                    ...message.updateData,
                  };
                } else {
                  // Add new room to flat array
                  updatedRooms.push(message.updateData);
                }

                // Update flat departments array if hierarchyAreas is provided
                let updatedDepartments = [...(prevData.departments || [])];
                if (message.updateData.hierarchyAreas) {
                  const deptIndex = updatedDepartments.findIndex(
                    (dept) =>
                      dept.pk_id ===
                      message.updateData.hierarchyAreas.departmentId,
                  );
                  if (deptIndex !== -1) {
                    updatedDepartments[deptIndex] = {
                      ...updatedDepartments[deptIndex],
                      department_area:
                        message.updateData.hierarchyAreas.totalArea,
                      department_baseline_cleaning_area:
                        message.updateData.hierarchyAreas.totalBaselineArea,
                    };
                  }
                }

                return {
                  ...prevData,
                  locations: updatedLocations,
                  rooms: updatedRooms,
                  departments: updatedDepartments,
                };
              });
              setLastUpdated(new Date());
              showSnackbar(
                message.updateData.hierarchyAreas
                  ? "Room and department areas updated"
                  : "Room data updated",
                "info",
              );
            }, 0);
          }
          break;
        case "robot_status_updated":
          // Don't update customer data for robot updates, just show notification
          console.log("Robot status updated:", message.updateData);
          showSnackbar("Robot status updated", "info");
          break;
        default:
          console.log(`Unknown message type: ${message.type}, ignoring.`);
      }
    },
    [customerId, customerData, showSnackbar],
  );

  // Subscribe to global SSE using our custom hook
  const {
    isConnected,
    error: sseError,
    lastEventTime,
    conflictDetector,
  } = useSSESubscription(
    `customer_page_${customerId}`, // Unique subscriber ID
    handleSSEMessage,
    [customerId], // Re-subscribe if customerId changes
  );

  const fetchServiceMetadata = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAllServiceListTypes();
      setListTypes(data.result);
      console.log("Service metadata:", data.result);
    } catch (err) {
      //console.error("Error loading service metadata:", err.message);
      setError("Failed to load service metadata");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchServiceMetadata();
  }, []);

  useEffect(() => {
    //console.log(
    //  `🎯 [CustomerPage] useEffect fetchCustomer triggered, customerId: ${customerId}`,
    //);
    if (customerId) {
      fetchCustomer();
    } else {
      console.warn(`⚠️ [CustomerPage] No customerId provided, skipping fetch`);
    }
  }, [customerId]);

  // Add missing helper to map type id -> type name
  const getLocationTypeName = (typeId) => {
    if (!listTypes?.location_types) return "";
    const found = listTypes.location_types.find((t) => t.pk_id === typeId);
    return found ? found.type_name : "";
  };

  const handleCreateLocation = () => {
    setModalOpen(true);
  };

  const handleSaveCustomer = async (customerData) => {
    try {
      // Start tracking edits
      conflictDetector.startEditing(
        "customer",
        customerData.pk_id,
        customerData,
      );

      console.log("Saving customer");
      const payload = {
        ...customerData,
        customer_name: customerData.uk_name,
      };
      const customer_pk_id = customerData.pk_id;
      const res = await updateCustomer(customer_pk_id, payload);
      console.log("Customer update response:", res);

      showSnackbar("Customer updated successfully", "success");
    } catch (err) {
      console.error("Failed to save customer:", err);
      showSnackbar(err.message || "Failed to save customer", "error");
      throw err;
    } finally {
      // Stop tracking edits
      conflictDetector.stopEditing("customer", customerData?.pk_id);
    }
  };

  const handleSaveLocation = async (locationData) => {
    const locationId = locationData.pk_id || "new";
    try {
      conflictDetector.startEditing("location", locationId, locationData);

      const payload = {
        ...locationData,
        customer_pk_id: customerData.customer.pk_id,
        customer_name: customerData.customer.uk_name,
      };
      console.log("Saving location with payload:", payload);

      const res = await upsertLocation(payload);
      if (res.success) {
        showSnackbar("Location saved successfully", "success");
      } else {
        showSnackbar(res.error || "Failed to save location", "error");
      }
    } catch (err) {
      console.error("Failed to save location:", err);
      showSnackbar(err.message || "Failed to save location", "error");
      throw err;
    } finally {
      conflictDetector.stopEditing("location", locationId);
    }
  };

  const handleSaveBuilding = async (updatedBuilding) => {
    try {
      const payload = {
        ...updatedBuilding,
        customer_pk_id: customerData.customer.pk_id,
        customer_name: customerData.customer.uk_name,
      };
      console.log("Save building:", payload);

      const res = await upsertBuilding(payload);
      if (res.success) {
        showSnackbar("Building saved successfully", "success");
      } else {
        showSnackbar(res.error || "Failed to save building", "error");
      }
    } catch (err) {
      console.error("Failed to save building:", err);
      showSnackbar(err.message || "Failed to save building", "error");
      throw err;
    }
  };

  const handleSaveDepartment = async (updatedDepartment) => {
    console.log("Attempting to save department:", updatedDepartment);
    try {
      const payload = {
        ...updatedDepartment,
        customer_pk_id: customerData.customer.pk_id,
        customer_name: customerData.customer.uk_name,
      };
      console.log("Save department:", payload);
      const res = await upsertDepartment(payload);
      if (res.success) {
        showSnackbar("Department saved successfully", "success");
      } else {
        showSnackbar(res.error || "Failed to save department", "error");
      }
    } catch (err) {
      console.error("Failed to save department:", err);
      showSnackbar(err.message || "Failed to save department", "error");
      throw err;
    }
  };

  const handleSaveRoom = async (updatedRoom) => {
    try {
      const payload = {
        ...updatedRoom,
        customer_pk_id: customerData.customer.pk_id,
        customer_name: customerData.customer.uk_name,
      };
      console.log("Save room:", payload);
      const res = await upsertRoom(payload);
      if (res.success) {
        showSnackbar("Room saved successfully", "success");
      } else {
        showSnackbar(res.error || "Failed to save room", "error");
      }
    } catch (err) {
      console.error("Failed to save room:", err);
      showSnackbar(err.message || "Failed to save room", "error");
      throw err;
    }
  };

  // Handle conflict resolution
  const handleResolveConflict = (conflictId, resolution, resolvedData) => {
    conflictDetector.resolveConflict(conflictId, resolution, resolvedData);

    if (resolution === "accept_server") {
      fetchCustomer();
      showSnackbar("Server data accepted", "info");
    } else if (resolution === "keep_client") {
      showSnackbar("Your changes preserved", "success");
    }

    setSelectedConflict(null);
  };

  return (
    <>
      <TopNavBar
        title="Customer Page"
        username={username}
        heading={
          loading
            ? "Customer"
            : customerData?.customer
              ? "Customer: " + customerData.customer.uk_name
              : "Customer"
        }
      />
      <Container maxWidth="xl" sx={{ paddingTop: 2 }}>
        {/* Connection Status */}
        <Box
          sx={{
            mb: 2,
            display: "flex",
            gap: 1,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Chip
              label={isConnected ? "Live Updates" : "Disconnected"}
              color={isConnected ? "success" : "error"}
              size="small"
              icon={isConnected ? <CheckCircleIcon /> : <WarningIcon />}
            />
            <Typography variant="caption" color="text.secondary">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </Typography>
            {lastEventTime && (
              <Typography variant="caption" color="text.secondary">
                • Last event: {lastEventTime.toLocaleTimeString()}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {/* Conflict indicator */}
            {conflictDetector.conflicts.length > 0 && (
              <Badge
                badgeContent={conflictDetector.conflicts.length}
                color="warning"
              >
                <Tooltip
                  title={`${conflictDetector.conflicts.length} edit conflicts detected`}
                >
                  <IconButton
                    size="small"
                    color="warning"
                    onClick={() =>
                      setSelectedConflict(conflictDetector.conflicts[0])
                    }
                  >
                    <WarningIcon />
                  </IconButton>
                </Tooltip>
              </Badge>
            )}

            {/* Manual refresh button */}
            <Tooltip title="Refresh data">
              <IconButton
                size="small"
                onClick={() => fetchCustomer()}
                disabled={loading}
              >
                <SyncIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {loading ? (
          <Grid container justifyContent="center">
            <CircularProgress />
          </Grid>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : !customerData ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography color="warning.main" variant="h6">
              No customer data available
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              Customer data is still loading or unavailable
            </Typography>
          </Box>
        ) : !customerData.customer ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography color="error" variant="h6">
              Invalid customer data structure
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              Customer object is missing from response
            </Typography>
          </Box>
        ) : (
          <Grid container columns={16} spacing={2}>
            <Grid size={{ xs: 16, md: 7, lg: 5 }}>
              <CustomerCard
                customer={customerData?.customer}
                onSave={handleSaveCustomer}
                isEditing={conflictDetector.isEditing(
                  "customer",
                  customerData?.customer?.pk_id,
                )}
              />
            </Grid>
            <Grid size={{ xs: 16, md: 9, lg: 11 }}>
              <LocationsCollapseTable
                locations={customerData?.locations || []}
                buildings={customerData?.buildings || []}
                departments={customerData?.departments || []}
                rooms={customerData?.rooms || []}
                onCreateLocation={handleCreateLocation}
                onSaveLocation={handleSaveLocation}
                onSaveBuilding={handleSaveBuilding}
                onSaveDepartment={handleSaveDepartment}
                onSaveRoom={handleSaveRoom}
                getLocationTypeName={getLocationTypeName}
                locationTypes={listTypes?.location_types || []}
                departmentTypes={listTypes?.department_types || []}
                roomTypes={listTypes?.room_types || []}
                floorTypes={listTypes?.floor_types || []}
                editingStates={conflictDetector.editingStates}
              />
            </Grid>
          </Grid>
        )}

        {modalOpen && (
          <LocationModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            onSave={handleSaveLocation}
            listTypes={listTypes?.location_types || []}
          />
        )}

        {/* Conflict Resolution Dialog */}
        <ConflictResolutionDialog
          conflict={selectedConflict}
          open={!!selectedConflict}
          onResolve={handleResolveConflict}
          onClose={() => setSelectedConflict(null)}
        />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </>
  );
}
